import CollectorCard from '../passport/CollectorCard';
import { sourceCard, vehicleSourceCard } from '../passport/sourceCards';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckBadgeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/ApiService';
import type {
  GlobalSearchResult,
  GlobalSearchResults as GlobalSearchResultsMap,
} from '../../types/buildExpansion';
import PageShell from '../ui/PageShell';
import { PageHeader } from '../ui/PageHeader';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';

const MIN_QUERY_LENGTH = 2;
const RESULTS_PER_GROUP = 10;

const GROUP_LABELS: { key: string; label: string }[] = [
  { key: 'vehicles', label: 'Vehicles' },
  { key: 'modifications', label: 'Modifications' },
  { key: 'brands', label: 'Brands' },
  { key: 'shops', label: 'Shops' },
  { key: 'members', label: 'Members' },
  { key: 'clubs', label: 'Clubs' },
  { key: 'events', label: 'Events' },
  { key: 'awards', label: 'Awards' },
  { key: 'achievements', label: 'Achievements' },
];

const prettifyKey = (key: string): string =>
  key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

interface OrderedGroup {
  key: string;
  label: string;
  items: GlobalSearchResult[];
}

const orderGroups = (results: GlobalSearchResultsMap): OrderedGroup[] => {
  const groups: OrderedGroup[] = [];
  for (const { key, label } of GROUP_LABELS) {
    const items = results[key];
    if (Array.isArray(items) && items.length > 0) groups.push({ key, label, items });
  }
  const knownKeys = new Set(GROUP_LABELS.map((g) => g.key));
  for (const key of Object.keys(results)) {
    const items = results[key];
    if (!knownKeys.has(key) && Array.isArray(items) && items.length > 0) {
      groups.push({ key, label: prettifyKey(key), items });
    }
  }
  return groups;
};

const isExternalUrl = (url: string): boolean => /^https?:\/\//i.test(url);

const ResultCard: React.FC<{ result: GlobalSearchResult }> = ({ result }) => {
  const kind =
    result.type === 'vehicle' ||
    result.type === 'event' ||
    result.type === 'club' ||
    result.type === 'shop' ||
    result.type === 'place'
      ? result.type
      : null;
  if (kind && result.id)
    return (
      <CollectorCard
        card={
          kind === 'vehicle'
            ? vehicleSourceCard({
                id: result.id,
                display_name: result.title,
                image_url: result.image_url || undefined,
                card_projection: result.card_projection,
              })
            : sourceCard({
                kind,
                id: result.id,
                title: result.title,
                subtitle: result.subtitle,
                image: result.image_url,
              })
        }
        href={result.url || undefined}
        fullRecordExtension={
          <>{result.verified && <p className="text-xs text-orange-300">Verified</p>}</>
        }
      />
    );
  const inner = (
    <div className="flex items-center gap-3 p-3">
      {result.image_url ? (
        <img
          src={apiService.getFileUrl(result.image_url) || result.image_url}
          alt=""
          className="w-12 h-12 rounded-lg object-cover border border-white/[0.08] bg-[#0a0a0a] shrink-0"
        />
      ) : (
        <div
          className="w-12 h-12 rounded-lg bg-[#141414] border border-white/[0.08] flex items-center justify-center text-sm font-semibold text-gray-400 shrink-0"
          aria-hidden
        >
          {(result.title || '?').charAt(0).toUpperCase()}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-white flex items-center gap-1.5">
          <span className="truncate">{result.title}</span>
          {result.verified ? (
            <span className="inline-flex items-center text-orange-300 shrink-0">
              <CheckBadgeIcon className="w-4 h-4" aria-hidden />
              <span className="sr-only">Verified</span>
            </span>
          ) : null}
        </p>
        {result.subtitle ? (
          <p className="text-xs text-gray-500 truncate mt-0.5">{result.subtitle}</p>
        ) : null}
      </div>
    </div>
  );

  if (!result.url) {
    return <div className="card">{inner}</div>;
  }
  if (isExternalUrl(result.url)) {
    return (
      <a
        href={result.url}
        rel="noopener noreferrer"
        className="card-interactive block cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 rounded-xl"
      >
        {inner}
      </a>
    );
  }
  return (
    <Link
      to={result.url}
      className="card-interactive block cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 rounded-xl"
    >
      {inner}
    </Link>
  );
};

/**
 * URL-addressable full search results page: reads q from the query string
 * (e.g. /search?q=supra) and renders grouped results with larger limits.
 */
export const GlobalSearchResultsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = (searchParams.get('q') || '').trim();

  const [input, setInput] = useState(q);
  const [results, setResults] = useState<GlobalSearchResultsMap | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    setInput(q);
  }, [q]);

  useEffect(() => {
    if (q.length < MIN_QUERY_LENGTH) {
      setResults(null);
      setError(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const response = await apiService.globalSearch(q, RESULTS_PER_GROUP);
        if (!cancelled) setResults(response);
      } catch (e: unknown) {
        if (!cancelled) {
          setResults(null);
          setError(e instanceof Error ? e.message : 'Search failed. Try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [q, retryKey]);

  const groups = useMemo(() => (results ? orderGroups(results) : []), [results]);
  const totalResults = useMemo(() => groups.reduce((sum, g) => sum + g.items.length, 0), [groups]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = input.trim();
    if (next.length >= MIN_QUERY_LENGTH) {
      setSearchParams({ q: next });
    }
  };

  return (
    <PageShell width="md">
      <PageHeader
        eyebrow="Find"
        title="Search"
        description={
          q
            ? `Results for “${q}” across builds, shops, members, clubs, and events.`
            : 'Search builds, shops, members, clubs, and events.'
        }
        backLink={{ label: 'Back to Discover', to: '/discover' }}
      />

      <form className="flex gap-2 mb-8" onSubmit={onSubmit} role="search">
        <input
          type="search"
          className="input flex-1"
          placeholder="Search builds, shops, members…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          aria-label="Search Bilt:Spec"
          autoComplete="off"
        />
        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>

      <div aria-live="polite" className="sr-only">
        {loading
          ? 'Searching…'
          : results
            ? totalResults === 1
              ? '1 result'
              : `${totalResults} results`
            : ''}
      </div>

      {q.length < MIN_QUERY_LENGTH ? (
        <EmptyState
          icon={<MagnifyingGlassIcon className="w-14 h-14" />}
          title="Search Bilt:Spec"
          description={`Enter at least ${MIN_QUERY_LENGTH} characters to search vehicles, modifications, brands, shops, members, clubs, events, awards, and achievements.`}
        />
      ) : loading ? (
        <div role="status" aria-busy="true" className="space-y-6">
          <span className="sr-only">Searching…</span>
          {Array.from({ length: 3 }).map((_, sectionIdx) => (
            <div key={sectionIdx}>
              <div className="h-4 w-28 rounded bg-[#141414] mb-3 animate-pulse motion-reduce:animate-none" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                {Array.from({ length: 4 }).map((_, cardIdx) => (
                  <div
                    key={cardIdx}
                    className="h-[72px] rounded-xl border border-white/[0.06] bg-[#0c0c0c]/70 animate-pulse motion-reduce:animate-none"
                    aria-hidden
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <EmptyState
          icon={<MagnifyingGlassIcon className="w-14 h-14" />}
          title="Search failed"
          description={error}
          action={
            <Button type="button" variant="primary" onClick={() => setRetryKey((k) => k + 1)}>
              Try again
            </Button>
          }
        />
      ) : totalResults === 0 ? (
        <EmptyState
          icon={<MagnifyingGlassIcon className="w-14 h-14" />}
          title={`No results for “${q}”`}
          description="Try a different make, model, shop name, or username."
        />
      ) : (
        <div className="space-y-10">
          {groups.map((group) => (
            <section key={group.key} aria-labelledby={`search-group-${group.key}`}>
              <h2 id={`search-group-${group.key}`} className="heading-section mb-4">
                {group.label}
                <span className="ml-2 text-sm font-normal text-gray-500">
                  {group.items.length === 1 ? '1 result' : `${group.items.length} results`}
                </span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                {group.items.map((result, i) => (
                  <ResultCard key={`${group.key}-${result.id ?? i}`} result={result} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </PageShell>
  );
};

export default GlobalSearchResultsPage;
